import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useBPMNRenderer } from "../../../hooks/useBPMNRenderer";
import { Form, Col, Button } from "react-bootstrap";
import { FaPlay } from "react-icons/fa";

const TextareaContainer = () => {
  const [input, setInput] = useState("");
  const renderBPMN = useBPMNRenderer();
  const viewer = useSelector((state) => state.viewer);

  /**
   * Attempts to parse the input string as JSON.
   * - If the parsed result is an object, returns a deep copy of the input.
   * - Otherwise, returns the parsed value directly.
   * 
   * @param {string} input - The user input string to parse.
   * @returns {Promise<any>} - The parsed JSON object or value.
   * @throws {SyntaxError} - If the input is not valid JSON.
   */
  const parseJSONInput = async (input) => {
    if (typeof JSON.parse(input) === "object") {
      return JSON.parse(JSON.stringify(input));
    } else {
      return JSON.parse(input);
    }
  };

  /**
   * Processes the user input from the textarea.
   * - First, tries to parse the input as JSON and render it as BPMN.
   * - If JSON parsing fails, assumes the input is BPMN XML and tries to render it.
   * - If both attempts fail, clears the viewer and alerts the user.
   */
  const processUserInput = async () => {
    try {
      // Try to parse the input as JSON and render BPMN from JSON
      const jsonData = await parseJSONInput(input);
      await renderBPMN({ jsonData });
    } catch {
      // If JSON parsing fails, try to render as BPMN XML
      try {
        await renderBPMN({ xmlData: input });
      } catch (error) {
        // If both JSON and XML parsing fail, clear the viewer and alert the user
        console.error(error);
        viewer.clear();
        alert(
          "Your input is not parsable JSON or BPMN XML. Check console logs for more details."
        );
      }
    }
  };

  return (
    <div id="chat-container" className="bg-gray ">
      <Col className="chat-col" xs={12}>
        <Form.Control
          as="textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Enter your JSON or BPMN-XML"
          id="textarea-field"
        />
        <Button
          variant="dark"
          size="lg"
          className="mt-2"
          onClick={processUserInput}
          disabled={input.trim().length === 0}
        >
          <FaPlay />
        </Button>
      </Col>
    </div>
  );
};

export default TextareaContainer;
